import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, errMsg, fmtDate } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';
import MeetingsTab from './MeetingsTab';

const TYPES = { strategy: 'الاستراتيجيات', policy: 'السياسات', objective: 'الأهداف', achievement: 'الإنجازات' };
const STATUS = { draft: 'مسودة', active: 'فعّال', archived: 'مؤرشف' };
const STATUS_CLS = { draft: 'bg-amber-50 text-amber-700', active: 'bg-teal-50 text-teal-700', archived: 'bg-slate-100 text-slate-500' };
const ACHIEVEMENT_CATEGORIES = { local: 'محلي', regional: 'إقليمي', national: 'وطني', international: 'دولي', other: 'أخرى' };
const TAB_ICONS = { strategy: 'flag', policy: 'document', objective: 'star', achievement: 'trophy' };

export default function GovernancePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('meeting');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/governance-items`, auth()); setItems(Array.isArray(res.data) ? res.data : []); } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (it) => {
    setEditing(it || null);
    if (it) {
      setForm({ ...it, images: it.images && it.images.length > 0 ? it.images : [''] });
    } else if (tab === 'achievement') {
      setForm({ title: '', description: '', status: 'active', achievement_year: '', achievement_category: '', images: [''] });
    } else {
      setForm({ title: '', description: '', status: 'active', owner: '', target_date: '' });
    }
    setErr(null); setModal(true);
  };

  const addImage = () => setForm((f) => ({ ...f, images: [...(f.images || ['']), ''] }));
  const removeImage = (i) => setForm((f) => ({ ...f, images: (f.images || ['']).filter((_, idx) => idx !== i) }));
  const updateImage = (i, v) => setForm((f) => ({ ...f, images: (f.images || ['']).map((img, idx) => (idx === i ? v : img)) }));

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.title?.trim()) { setErr('العنوان مطلوب'); return; }
    const base = { title: form.title.trim(), description: form.description || null, status: form.status || 'active' };
    const payload = tab === 'achievement'
      ? { ...base, achievement_year: form.achievement_year || null, achievement_category: form.achievement_category || null, images: (form.images || []).map((s) => s.trim()).filter(Boolean) }
      : { ...base, owner: form.owner || null, target_date: form.target_date || null };
    try {
      if (editing) await axios.put(`${API}/governance-items/${editing.id}`, payload, auth());
      else await axios.post(`${API}/governance-items`, { ...payload, item_type: tab }, auth());
      setModal(false); notify('تم الحفظ بنجاح'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/governance-items/${confirmDel.id}`, auth()); notify('تم الحذف'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const visible = items.filter((i) => i.item_type === tab);

  return (
    <div className="mx-auto max-w-6xl space-y-4" data-testid="governance-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      {tab !== 'meeting' && (
        <PageHead title="الحوكمة والإنجازات" desc="استراتيجيات المنشأة وسياساتها وأهدافها وإنجازاتها في مكان واحد"
          action={<PrimaryBtn onClick={() => openForm(null)} data-testid="gov-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة {TYPES[tab].replace('ال', '').slice(0, -2) || 'عنصر'}</PrimaryBtn>} />
      )}

      <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1" data-testid="gov-tabs">
        <button onClick={() => setTab('meeting')} className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${tab === 'meeting' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="gov-tab-meeting">
          الاجتماعات والقرارات
        </button>
        {Object.entries(TYPES).map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${tab === k ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`gov-tab-${k}`}>
            {v} ({items.filter((i) => i.item_type === k).length})
          </button>
        ))}
      </div>

      {tab === 'meeting' ? <MeetingsTab /> : loading ? <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}</div>
        : visible.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-8"><EmptyState icon={TAB_ICONS[tab]} text={`لا توجد عناصر في ${TYPES[tab]} بعد`} testId="gov-empty" /></div>
        : (
          <div className="space-y-2">
            {visible.map((it) => (
              <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="gov-item">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-900">{it.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_CLS[it.status] || ''}`}>{STATUS[it.status] || it.status}</span>
                    </div>
                    {it.description && <p className="mt-1 text-sm text-slate-500">{it.description}</p>}
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
                      {it.item_type === 'achievement' ? (
                        <>
                          {it.achievement_year && <span>السنة: {it.achievement_year}</span>}
                          {it.achievement_category && <span>التصنيف: {ACHIEVEMENT_CATEGORIES[it.achievement_category] || it.achievement_category}</span>}
                        </>
                      ) : (
                        <>
                          {it.owner && <span>المسؤول: {it.owner}</span>}
                          {it.target_date && <span>الموعد المستهدف: {fmtDate(it.target_date)}</span>}
                        </>
                      )}
                      <span>أضيف بواسطة: {it.created_by || '—'}</span>
                    </div>
                    {it.item_type === 'achievement' && Array.isArray(it.images) && it.images.filter(Boolean).length > 0 && (
                      <div className="mt-2 flex gap-2" data-testid="gov-achievement-images">
                        {it.images.filter(Boolean).slice(0, 5).map((img, idx) => (
                          <img key={idx} src={img} alt={it.title} className="h-14 w-14 rounded-lg border border-slate-200 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openForm(it)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="gov-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                    <button onClick={() => setConfirmDel(it)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="gov-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <Modal open={modal} title={editing ? 'تعديل العنصر' : `إضافة إلى ${TYPES[tab]}`} onClose={() => setModal(false)} wide>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="gov-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="العنوان" required><input className={inputCls} value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gov-input-title" /></Field>
          <Field label="الوصف"><textarea rows={3} className={inputCls} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>

          {tab === 'achievement' ? (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="الحالة">
                  <select className={inputCls} value={form.status || 'active'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </Field>
                <Field label="سنة الإنجاز"><input className={inputCls} placeholder="مثال: 2026" value={form.achievement_year || ''} onChange={(e) => setForm({ ...form, achievement_year: e.target.value })} data-testid="gov-input-achievement-year" /></Field>
                <Field label="التصنيف">
                  <select className={inputCls} value={form.achievement_category || ''} onChange={(e) => setForm({ ...form, achievement_category: e.target.value })} data-testid="gov-input-achievement-category">
                    <option value="">— بلا تصنيف —</option>
                    {Object.entries(ACHIEVEMENT_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="صور الإنجاز (روابط)">
                <div className="space-y-2">
                  {(form.images || ['']).map((img, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className={inputCls} placeholder="https://example.com/image.jpg" value={img} onChange={(e) => updateImage(i, e.target.value)} data-testid={`gov-achievement-image-input-${i}`} />
                      {(form.images || ['']).length > 1 && (
                        <button type="button" onClick={() => removeImage(i)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`gov-achievement-image-remove-${i}`}><Icon name="trash" className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addImage} className="text-xs font-semibold text-teal-700 hover:text-teal-800" data-testid="gov-achievement-image-add">+ إضافة رابط صورة</button>
                </div>
              </Field>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="الحالة">
                <select className={inputCls} value={form.status || 'active'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="المسؤول"><input className={inputCls} value={form.owner || ''} onChange={(e) => setForm({ ...form, owner: e.target.value })} data-testid="gov-input-owner" /></Field>
              <Field label="الموعد المستهدف"><input type="date" className={inputCls} value={form.target_date ? String(form.target_date).slice(0, 10) : ''} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="gov-input-target-date" /></Field>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="gov-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف العنصر" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف «{confirmDel?.title}»؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="gov-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}
